import { SlidersHorizontal, Bot, AudioLines, BookOpen, Store, Upload, ShieldAlert } from 'lucide-react';

// Curated, operator facing settings catalog. Only the keys listed here are
// shown; everything else stays internal. Each field maps to a Setting key
// and declares the control to render. No raw JSON is ever exposed.

export const SECTIONS = [
  {
    id: 'general',
    label: 'Generale',
    icon: SlidersHorizontal,
    fields: [
      { key: 'system.maintenanceMode', control: 'toggle', label: 'Modalità manutenzione', help: 'Il sistema risponde 503 a tutti tranne gli IP autorizzati.' },
      { key: 'system.maintenanceMessage', control: 'text', label: 'Messaggio di manutenzione' },
      { key: 'system.maintenanceBypassIps', control: 'tags', label: 'IP autorizzati in manutenzione', help: 'Indirizzi che possono usare il sistema durante la manutenzione.' },
      { key: 'cache.enabled', control: 'toggle', label: 'Cache delle risposte' },
      { key: 'cache.defaultTtlSec', control: 'number', label: 'Durata cache', unit: 's', min: 0 }
    ]
  },
  {
    id: 'ai',
    label: 'AI',
    icon: Bot,
    note: { text: 'Modelli e prezzi si gestiscono nella pagina dedicata.', linkLabel: 'Vai a Modelli', to: '/admin/ai/models' },
    fields: [
      { key: 'ai.openai.apiKey', control: 'password', label: 'Chiave API OpenAI', help: 'Cifrata a riposo. Inserisci una nuova chiave per sostituirla.' },
      { key: 'ai.openai.baseUrl', control: 'text', label: 'Base URL provider', help: 'Cambia per usare un provider compatibile (Azure, ecc.).' },
      { key: 'ai.defaultModel', control: 'modelSelect', modelKind: 'chat', label: 'Modello chat predefinito', help: 'Usato per le risposte testuali quando un agente non specifica il proprio.' },
      { key: 'ai.embeddingsModel', control: 'modelSelect', modelKind: 'embedding', label: 'Modello embeddings (RAG)' },
      { key: 'ai.transcribeModel', control: 'modelSelect', modelKind: 'transcription', label: 'Modello trascrizione (dettatura)' },
      { key: 'ai.defaultTemperature', control: 'number', label: 'Temperatura', help: 'Creatività delle risposte (0 = deterministico, 2 = molto creativo).', min: 0, max: 2, step: 0.1 },
      { key: 'ai.maxTokensPerRequest', control: 'number', label: 'Token massimi per risposta', unit: 'token', min: 1 },
      { key: 'ai.budget.monthlyUsdCap', control: 'number', label: 'Tetto di spesa mensile', unit: 'USD', min: 0, help: '0 per disattivare il limite. I prezzi dei modelli sono in dollari.' },
      { key: 'ai.budget.warnAtPercent', control: 'number', label: 'Avviso al raggiungimento', unit: '%', min: 0, max: 100 },
      { key: 'ai.budget.behaviorOnExceeded', control: 'select', label: 'Al superamento del tetto', options: [{ value: 'block', label: 'Blocca le richieste' }, { value: 'notify-only', label: 'Solo notifica' }] },
      { key: 'ai.agents.maxIterations', control: 'number', label: 'Iterazioni massime agente', min: 1 },
      { key: 'ai.agents.historyLimit', control: 'number', label: 'Messaggi di storico nel contesto', min: 1 }
    ]
  },
  {
    id: 'voice',
    label: 'Voce',
    icon: AudioLines,
    note: { text: 'Il modello vocale si gestisce nella pagina Modelli.', linkLabel: 'Vai a Modelli', to: '/admin/ai/models' },
    fields: [
      { key: 'voice.enabled', control: 'toggle', label: 'Assistente vocale attivo' },
      { key: 'voice.model', control: 'modelSelect', modelKind: 'realtime', label: 'Modello vocale (realtime)' },
      { key: 'voice.voice', control: 'select', label: 'Voce', options: ['alloy', 'ash', 'ballad', 'coral', 'echo', 'sage', 'shimmer', 'verse'].map((v) => ({ value: v, label: v })) },
      { key: 'voice.language', control: 'text', label: 'Lingua', help: 'Codice lingua, es. it.' },
      { key: 'voice.maxSessionMinutes', control: 'number', label: 'Durata massima sessione', unit: 'min', min: 1 },
      { key: 'voice.idleTimeoutSec', control: 'number', label: 'Timeout di inattività', unit: 's', min: 5 },
      { key: 'voice.budget.minutesPerStorePerDay', control: 'number', label: 'Minuti vocali al giorno per negozio', unit: 'min', min: 0 },
      { key: 'voice.persistTranscript', control: 'toggle', label: 'Salva le trascrizioni vocali' }
    ]
  },
  {
    id: 'rag',
    label: 'Knowledge base',
    icon: BookOpen,
    fields: [
      { key: 'rag.topK', control: 'number', label: 'Risultati per ricerca', help: 'Quanti frammenti recuperare per ogni domanda.', min: 1 },
      { key: 'rag.minScore', control: 'number', label: 'Soglia di pertinenza minima', help: 'Da 0 a 1: scarta i risultati sotto questo punteggio.', min: 0, max: 1, step: 0.05 },
      { key: 'rag.chunkSize', control: 'number', label: 'Dimensione frammento', unit: 'caratteri', min: 200 },
      { key: 'rag.chunkOverlap', control: 'number', label: 'Sovrapposizione frammenti', unit: 'caratteri', min: 0 },
      { key: 'rag.embeddingBatchSize', control: 'number', label: 'Frammenti per chiamata di indicizzazione', min: 1 }
    ]
  },
  {
    id: 'stores',
    label: 'Negozi',
    icon: Store,
    fields: [
      { key: 'tenantScope.appliedRoles', control: 'rolesMulti', label: 'Ruoli vincolati al negozio', help: 'Owner e admin vedono sempre tutti i negozi.' },
      { key: 'kiosk.consentTtlSec', control: 'number', label: 'Durata consenso cliente kiosk', unit: 's', min: 60 }
    ]
  },
  {
    id: 'upload',
    label: 'Upload',
    icon: Upload,
    fields: [
      { key: 'upload.maxFileSizeBytes', control: 'bytes', label: 'Dimensione massima file', unit: 'MB', min: 1 },
      { key: 'upload.acceptedMimeTypes', control: 'tags', label: 'Tipi di file accettati (MIME)', lowercase: true }
    ]
  },
  {
    id: 'advanced',
    label: 'Avanzate',
    icon: ShieldAlert,
    advanced: true,
    groups: [
      {
        label: 'Rate limiting',
        help: 'Quante richieste sono ammesse per finestra temporale.',
        fields: [
          { key: 'security.rateLimit.general.max', control: 'number', label: 'Globale: richieste', unit: 'req', min: 1 },
          { key: 'security.rateLimit.general.windowMs', control: 'number', label: 'Globale: finestra', unit: 'ms', min: 1000 },
          { key: 'security.rateLimit.auth.max', control: 'number', label: 'Login: richieste', unit: 'req', min: 1 },
          { key: 'security.rateLimit.auth.windowMs', control: 'number', label: 'Login: finestra', unit: 'ms', min: 1000 },
          { key: 'security.rateLimit.ai.max', control: 'number', label: 'AI: richieste', unit: 'req', min: 1 },
          { key: 'security.rateLimit.ai.windowMs', control: 'number', label: 'AI: finestra', unit: 'ms', min: 1000 }
        ]
      },
      {
        label: 'Protezione minacce',
        help: 'Punteggio di sospetto, ban e liste di filtro.',
        fields: [
          { key: 'security.threat.scoreWindowSec', control: 'number', label: 'Finestra punteggio sospetti', unit: 's', min: 1 },
          { key: 'security.threat.shadowBanThreshold', control: 'number', label: 'Soglia shadow ban', min: 1 },
          { key: 'security.threat.hardBanThreshold', control: 'number', label: 'Soglia hard ban', min: 1 },
          { key: 'security.threat.banDurationsSec', control: 'tagsNum', label: 'Scala durate ban', unit: 's' },
          { key: 'security.threat.suspiciousPaths', control: 'tags', label: 'Path sospetti' },
          { key: 'security.threat.maliciousUserAgents', control: 'tags', label: 'User-Agent sospetti' },
          { key: 'security.threat.allowlist', control: 'tags', label: 'IP in allowlist (bypass)' }
        ]
      },
      {
        label: 'Blocco accessi',
        help: 'Protezione contro tentativi di login ripetuti e falliti.',
        fields: [
          { key: 'auth.lockout.maxFails', control: 'number', label: 'Tentativi prima del blocco', min: 1 },
          { key: 'auth.lockout.windowSec', control: 'number', label: 'Finestra di conteggio', unit: 's', min: 1 },
          { key: 'auth.lockout.lockSec', control: 'number', label: 'Durata del blocco', unit: 's', min: 1 }
        ]
      },
      {
        label: 'Autenticazione',
        fields: [
          { key: 'auth.mfa.stepUpMaxAgeSec', control: 'number', label: 'Validità verifica MFA (step up)', unit: 's', min: 1 },
          { key: 'auth.webauthn.rpName', control: 'text', label: 'WebAuthn: nome Relying Party' },
          { key: 'auth.webauthn.rpID', control: 'text', label: 'WebAuthn: Relying Party ID' },
          { key: 'auth.webauthn.origin', control: 'text', label: 'WebAuthn: origin atteso' }
        ]
      },
      {
        label: 'OAuth client',
        fields: [
          { key: 'oauth.access.ttlSec', control: 'number', label: 'Durata access token', unit: 's', min: 60 },
          { key: 'oauth.refresh.ttlSec', control: 'number', label: 'Durata refresh token', unit: 's', min: 60 }
        ]
      },
      {
        label: 'AI: affidabilità',
        help: 'Timeout, retry e circuit breaker delle chiamate al provider.',
        fields: [
          { key: 'ai.timeoutMs', control: 'number', label: 'Timeout chiamata', unit: 'ms', min: 1000 },
          { key: 'ai.retry.maxAttempts', control: 'number', label: 'Tentativi massimi', min: 1 },
          { key: 'ai.retry.baseDelayMs', control: 'number', label: 'Ritardo base retry', unit: 'ms', min: 0 },
          { key: 'ai.circuitBreaker.errorsThreshold', control: 'number', label: 'Circuit breaker: soglia errori', min: 1 },
          { key: 'ai.circuitBreaker.openSec', control: 'number', label: 'Circuit breaker: durata apertura', unit: 's', min: 1 }
        ]
      },
      {
        label: 'Knowledge base: indicizzazione',
        help: 'Parametri dell\'indice vettoriale Atlas.',
        fields: [
          { key: 'rag.indexName', control: 'text', label: 'Nome indice vettoriale' },
          { key: 'rag.numCandidates', control: 'number', label: 'Candidati di ricerca', min: 1 },
          { key: 'rag.embeddingsDimensions', control: 'number', label: 'Dimensioni embedding', min: 1 }
        ]
      }
    ]
  }
];

export const BYTES_PER_MB = 1024 * 1024;
